import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useAction } from "convex/react";
import { api } from "@/convex/_generated/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  KeyRound,
  ShieldCheck,
  Check,
  Building2,
  Mail,
  Phone,
  Hash,
  Users,
  FileText,
} from "lucide-react";
import { AuthMobileLogo, AuthRightPanel } from "@/components/auth/auth-layout";
import { LeftPanel } from "@/components/layout/left-panel";

export const Route = createFileRoute("/_public/licenses")({
  component: LicensesPage,
});

const licenseFeatures = [
  { icon: Building2, label: "Tus datos en tu servidor" },
  { icon: ShieldCheck, label: "Licencia firmada criptográficamente" },
  { icon: Users, label: "Tiers Starter, Pro y All" },
  { icon: KeyRound, label: "Activación en menos de 5 minutos" },
];

const REASON_MESSAGES: Record<string, string> = {
  nombre_requerido: "Tu nombre es obligatorio",
  email_invalido: "El email no es válido",
  asientos_invalidos: "La cantidad de usuarios no es válida",
  sucursales_invalidas: "La cantidad de sucursales no es válida",
};

function reasonToMessage(reason: string): string {
  return REASON_MESSAGES[reason] ?? reason;
}

/** Detecta si la build corre on-prem: el Convex client apunta a una ruta
 * relativa o vacía (no hay cloud Convex al cual escribir). En ese caso el
 * form debe redirigir al cliente a app.heskala.com para pedir la licencia. */
function getIsOnPrem(): boolean {
  if (typeof import.meta === "undefined") return true;
  const url = (import.meta as { env?: { VITE_CONVEX_URL?: string } }).env
    ?.VITE_CONVEX_URL;
  if (!url) return true;
  if (url.startsWith("/") || !url.startsWith("http")) return true;
  return false;
}

function LicensesPage() {
  const requestLicense = useAction(api.system.licensing.actions.requestLicense);

  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [edition, setEdition] = useState<"onprem" | "desktop" | "saas">(
    "onprem",
  );
  const [type, setType] = useState<"lifetime" | "subscription">("lifetime");
  const [seats, setSeats] = useState(5);
  const [maxBranches, setMaxBranches] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const result = await requestLicense({
        customerName: customerName.trim(),
        customerEmail: customerEmail.trim(),
        customerPhone: customerPhone.trim() || undefined,
        companyName: companyName.trim() || undefined,
        edition,
        type,
        seats,
        maxBranches: maxBranches.trim() ? Number(maxBranches) : undefined,
        notes: notes.trim() || undefined,
      });
      if (result.ok) {
        setSubmitted(true);
        setCustomerName("");
        setCustomerEmail("");
        setCustomerPhone("");
        setCompanyName("");
        setMaxBranches("");
        setNotes("");
      } else {
        toast.error(reasonToMessage(result.reason));
      }
    } catch (err) {
      console.error("Error enviando solicitud de licencia:", err);
      toast.error(
        err instanceof Error ? err.message : "No se pudo enviar la solicitud",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (getIsOnPrem()) {
    return (
      <div className="flex min-h-screen w-full">
        <LeftPanel
          subtitle="Esta página solo está disponible en la nube"
          features={licenseFeatures}
          headline={""}
          badge={""}
        />
        <AuthRightPanel>
          <AuthMobileLogo />
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" /> Pedinos tu licencia
              </CardTitle>
              <CardDescription>
                Estás corriendo Heskala on-prem. La solicitud de licencia la
                hacés desde la nube.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                Andá a{" "}
                <a
                  className="font-mono underline"
                  href="https://app.heskala.com/licenses"
                >
                  app.heskala.com/licenses
                </a>{" "}
                desde cualquier device, completá el formulario y te mandamos el
                token firmado al mail.
              </p>
              <p>
                Después volvé acá y pegá el token en la pantalla de activación.
              </p>
            </CardContent>
          </Card>
        </AuthRightPanel>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full">
      <LeftPanel
        subtitle="Pídenos tu token de licencia firmado"
        features={licenseFeatures}
        headline={""}
        badge={""}
      />
      <AuthRightPanel>
        <AuthMobileLogo />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> Solicitar licencia
            </CardTitle>
            <CardDescription>
              Te respondemos al mail con el token firmado en pocas horas.
            </CardDescription>
          </CardHeader>
          {submitted ? (
            <CardContent>
              <Alert className="border-green-500/40 bg-green-500/10">
                <Check className="h-4 w-4 text-green-600" />
                <AlertTitle>Listo</AlertTitle>
                <AlertDescription>
                  Tu solicitud fue recibida. Te mandamos el token al mail en
                  pocas horas. Cuando lo tengas, pegalo en la pantalla de
                  activación de tu instalación on-prem.
                </AlertDescription>
              </Alert>
            </CardContent>
          ) : (
            <form onSubmit={onSubmit}>
              <CardContent className="space-y-3">
                <div>
                  <Label htmlFor="customerName">Tu nombre</Label>
                  <Input
                    id="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                    minLength={2}
                    autoComplete="name"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="customerEmail"
                    className="flex items-center gap-1.5"
                  >
                    <Mail className="h-3.5 w-3.5" /> Email de contacto
                  </Label>
                  <Input
                    id="customerEmail"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label
                      htmlFor="customerPhone"
                      className="flex items-center gap-1.5"
                    >
                      <Phone className="h-3.5 w-3.5" /> Teléfono
                    </Label>
                    <Input
                      id="customerPhone"
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      autoComplete="tel"
                    />
                  </div>
                </div>
                <div>
                  <Label
                    htmlFor="companyName"
                    className="flex items-center gap-1.5"
                  >
                    <Building2 className="h-3.5 w-3.5" /> Razón social
                  </Label>
                  <Input
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                    minLength={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Edición</Label>
                    <Select
                      value={edition}
                      onValueChange={(v) =>
                        setEdition(v as "onprem" | "desktop" | "saas")
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="onprem">On-prem</SelectItem>
                        <SelectItem value="desktop">Desktop</SelectItem>
                        <SelectItem value="saas">SaaS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tipo</Label>
                    <Select
                      value={type}
                      onValueChange={(v) =>
                        setType(v as "lifetime" | "subscription")
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lifetime">Vitalicia</SelectItem>
                        <SelectItem value="subscription">
                          Suscripción
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label
                      htmlFor="seats"
                      className="flex items-center gap-1.5"
                    >
                      <Users className="h-3.5 w-3.5" /> Usuarios
                    </Label>
                    <Input
                      id="seats"
                      type="number"
                      min={1}
                      value={seats}
                      onChange={(e) => setSeats(Number(e.target.value))}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="maxBranches">Sucursales</Label>
                    <Input
                      id="maxBranches"
                      type="number"
                      min={1}
                      value={maxBranches}
                      onChange={(e) => setMaxBranches(e.target.value)}
                      placeholder="Opcional"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes" className="flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" /> Notas
                  </Label>
                  <Input
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Enviando…" : "Enviar solicitud"}
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </AuthRightPanel>
    </div>
  );
}
